/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const templates = {
    product: document.body.classList.contains('product-template'),
  };

  /*****************************************************************************************************************/

  /**
   * Return only blog searches on blog page
   * This class extend the initial class and modify the query parameters
   * {@link https://shopify.dev/docs/api/ajax/reference/predictive-search}
   * @author MaxDesign
   */
  const predictiveSearchDrawerClass = customElements.get('predictive-search-drawer');
  if (predictiveSearchDrawerClass) {
    customElements.define(
      'blog-predictive-search-drawer',
      class extends predictiveSearchDrawerClass {
        constructor() {
          super();
        }
        async _doPredictiveSearch(term) {
          const response = await fetch(
            `${window.themeVariables.routes.predictiveSearchUrl}?q=${encodeURIComponent(
              term
            )}&resources[type]=article&section_id=predictive-search`,
            {
              signal: this.abortController.signal,
            }
          );
          const div = document.createElement('div');
          div.innerHTML = await response.text();
          return {
            hasResults: div.querySelector('.predictive-search__results-categories') !== null,
            html: div.firstElementChild.innerHTML,
          };
        }
      }
    );
  }

  /*****************************************************************************************************************/

  /**
   * Fix stacking context issue in desktop
   * because of sticky gallery customization
   * @author MaxDesign
   */
  if (templates.product) {
    document.addEventListener('openable-element:close', event => {
      if (event.target.nodeName == 'PRODUCT-IMAGE-ZOOM')
        event.target.closest('.product__media').removeAttribute('data-stack-context');
    });
    document.addEventListener('openable-element:open', event => {
      if (event.target.nodeName == 'PRODUCT-IMAGE-ZOOM')
        event.target.closest('.product__media').setAttribute('data-stack-context', '');
    });
  }

  /*****************************************************************************************************************/

  /**
   * Update header bubble state on cart udpate
   * call setter from header instance
   */
  document.addEventListener('cart:updated', e => {
    if (MaxDesign?.header) MaxDesign.header.cartCount = e.detail.item_count;
  });

  /*****************************************************************************************************************/

  /**
   * Klaviyo ATC event
   * @author MaxDesign
   */
  function addedToCart() {
    fetch(`${window.location.origin}/cart.js`).then(res =>
      res
        .clone()
        .json()
        .then(data => {
          var cart = {
            total_price: data.total_price / 100,
            $value: data.total_price / 100,
            total_discount: data.total_discount,
            original_total_price: data.original_total_price / 100,
            items: data.items,
          };
          if (typeof item !== 'undefined') cart = Object.assign(cart, item);
          klaviyo.track('Added to Cart', cart);
        })
    );
  }
  (function (ns, fetch) {
    ns.fetch = function () {
      const response = fetch.apply(this, arguments);
      response.then(res => {
        const isUrlEqual = window.location.origin.replace(/\/+$/, '') === res.url.replace(/\/+$/, ''); // take into account /discount urls
        if (`${window.location.origin}/cart/add.js`.includes(res.url) && !isUrlEqual) addedToCart();
      });
      return response;
    };
  })(window, window.fetch);

  /*****************************************************************************************************************/

  /**
   * Custom scrollbar design in mobile
   * Inspired from Impact theme
   * @author MaxDesign
   */
  class ScrollProgress extends HTMLElement {
    connectedCallback() {
      this.scrolledElement.addEventListener('scroll', this._throttle(this._updateScrollProgress.bind(this)));
      new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
    }
    _updateScrollProgress() {
      const scrollLeft = this.scrolledElement.scrollLeft;
      const advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
      this.style.setProperty('--scroll-progress', Math.max(0, Math.min(advancement, 1)).toFixed(6));
    }
    _throttle(callback) {
      return (...args) => requestAnimationFrame(() => callback(...args));
    }
    get scrolledElement() {
      return (this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute('observes')));
    }
  }
  customElements.get('scroll-progress') || customElements.define('scroll-progress', ScrollProgress);

  /*****************************************************************************************************************/
})();
